// Apply per-project custom branding to CSS variables. Idempotent — safe to
// call on every config load. The defaults in src/styles/tokens.css get
// overridden in the document root.

import type { ToolConfig } from '@shared/types';

const DEFAULT_BRAND = '#31686C';

/** WCAG luminance threshold — picks white or black text against a given bg. */
function getContrastText(hex: string): '#FFFFFF' | '#000000' {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.replace('#', ''));
  if (!m) return '#FFFFFF';
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  const toLin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
  return L > 0.5 ? '#000000' : '#FFFFFF';
}

export function applyBranding(config: ToolConfig | null): void {
  const root = document.documentElement;
  if (!config?.customBranding || !config.brandColor) {
    root.style.removeProperty('--brandcolor');
    root.style.removeProperty('--brandcolor-text');
    root.style.setProperty('--brandcolor', DEFAULT_BRAND);
    root.style.setProperty('--brandcolor-text', '#FFFFFF');
    return;
  }
  const color = config.brandColor;
  const text =
    config.brandTextColor && /^#?[0-9a-f]{3,8}$/i.test(config.brandTextColor)
      ? config.brandTextColor
      : getContrastText(color);
  root.style.setProperty('--brandcolor', color);
  root.style.setProperty('--brandcolor-text', text);
}

export function setDocumentTitle(projectName?: string | null): void {
  document.title = projectName ? `${projectName} — TRO Voice Studio` : 'TRO Voice Studio';
}
